import { useEffect, useMemo, useState } from 'react';
import { buildSummaryByArea } from './machineStatDataHelpers';
import { applyProductionGapExclusions, buildDefaultProductionGaps, buildProductionGaps, getExcludedMinutes, mergeProductionGaps } from './productionTimeHelpers';

import { EMPTY_ENTRIES } from './machineStatDataConstants';

export default function useProductionGapExclusions({ dataset, datasets, rangeMode, from, to, dataByArea, summaryByArea, production, stops = EMPTY_ENTRIES, excludeNoFlow = false }) {
  const [excludedKeys, setExcludedKeys] = useState(() => new Set());
  const areaStops = useMemo(() => stops.filter((stop) => stop.area_identifier === dataset?.key), [dataset?.key, stops]);
  const stopBounds = areaStops.map((stop) => `${stop.id}:${stop.start}:${stop.end}:${stop.exclude}`).join('|');
  const scope = `${dataset?.key}|${rangeMode}|${from}|${to}|${stopBounds}`;
  useEffect(() => setExcludedKeys(new Set()), [scope]);
  const points = dataByArea[dataset?.key] ?? EMPTY_ENTRIES;
  const last = points[points.length - 1];
  const start = Date.parse(points[0]?.date);
  const end = Date.parse(last?.date) + (last?.durationMinutes ?? 1) * 60_000;
  const entries = production.entries ?? EMPTY_ENTRIES;
  const noFlowEntries = excludeNoFlow ? entries : EMPTY_ENTRIES;
  const defaultGapsByArea = useMemo(
    () =>
      Object.fromEntries(
        datasets.map((item) => {
          const daily = production.byArea?.[item.key];
          return [
            item.key,
            buildDefaultProductionGaps(
              daily?.entries ?? EMPTY_ENTRIES,
              daily?.days,
              item.defaultExcludedTimes,
              stops.filter((stop) => stop.area_identifier === item.key)
            )
          ];
        })
      ),
    [datasets, production.byArea, stops]
  );
  const defaults = defaultGapsByArea[dataset?.key] ?? EMPTY_ENTRIES;
  const gaps = useMemo(
    () => (dataset?.allowGapExclusions || dataset?.allowStopAnnotations || defaults.length ? buildProductionGaps(entries, start, end, defaults, areaStops) : []),
    [areaStops, dataset?.allowGapExclusions, dataset?.allowStopAnnotations, defaults, end, entries, start]
  );
  const excludedGaps = useMemo(() => gaps.filter((gap) => Boolean(gap.defaultExcluded) !== excludedKeys.has(gap.key)), [excludedKeys, gaps]);
  const exclusionsByArea = useMemo(() => {
    if (!dataset || !Number.isFinite(start) || !Number.isFinite(end)) return defaultGapsByArea;
    // Keep full-day defaults outside the visible graph; clicks override only its visible gaps.
    const outside = defaults.flatMap((gap) => [
      { start: gap.start, end: Math.min(gap.end, start) },
      { start: Math.max(gap.start, end), end: gap.end }
    ]);
    return { ...defaultGapsByArea, [dataset.key]: mergeProductionGaps([...outside, ...excludedGaps]) };
  }, [dataset, defaultGapsByArea, defaults, end, excludedGaps, start]);
  const unassignedStopGaps = useMemo(() => {
    if (!dataset?.allowStopAnnotations) return EMPTY_ENTRIES;
    return gaps
      .filter((gap) => !gap.hasStop && Boolean(gap.defaultExcluded) === excludedKeys.has(gap.key))
      .flatMap((gap) => {
        const noFlow = noFlowEntries.filter(
          (entry) => entry.aggregateSpeed === 0 && entry.noFlowMinutes > 0 && Date.parse(entry.windowStart) < gap.end && Date.parse(entry.windowStart) + (entry.windowLength ?? 1) * 60_000 > gap.start
        );
        const boundaries = [
          ...new Set([
            gap.start,
            gap.end,
            ...noFlow.flatMap((entry) => [Date.parse(entry.windowStart), Date.parse(entry.windowStart) + (entry.windowLength ?? 1) * 60_000]).filter((time) => time > gap.start && time < gap.end)
          ])
        ].sort((a, b) => a - b);
        return boundaries.slice(0, -1).flatMap((start, index) => {
          const end = boundaries[index + 1];
          const minutes = (end - start) / 60_000 - getExcludedMinutes(start, end, EMPTY_ENTRIES, noFlow);
          return minutes > 0 ? [{ start, end, minutes }] : EMPTY_ENTRIES;
        });
      })
      .reduce((merged, gap) => {
        const previous = merged[merged.length - 1];
        if (previous?.end === gap.start) {
          previous.end = gap.end;
          previous.minutes += gap.minutes;
        } else merged.push(gap);
        return merged;
      }, []);
  }, [dataset?.allowStopAnnotations, excludedKeys, gaps, noFlowEntries]);
  const efficiencyDataByArea = useMemo(
    () =>
      datasets.reduce(
        (data, item) => applyProductionGapExclusions(data, item, exclusionsByArea[item.key] ?? EMPTY_ENTRIES, production.byArea?.[item.key]?.entries ?? EMPTY_ENTRIES, excludeNoFlow),
        dataByArea
      ),
    [dataByArea, datasets, exclusionsByArea, production.byArea, excludeNoFlow]
  );
  const adjustedSummaryByArea = useMemo(() => {
    const affected = datasets.filter((item) => efficiencyDataByArea[item.key] !== dataByArea[item.key]);
    if (!affected.length) return summaryByArea;
    const summaries = buildSummaryByArea(efficiencyDataByArea, affected);
    return {
      ...summaryByArea,
      ...Object.fromEntries(
        affected.map((item) => {
          const included = efficiencyDataByArea[item.key];
          const duration = included.reduce((sum, point) => sum + (point.durationMinutes ?? 1), 0);
          const speedCalculations = Object.fromEntries(
            ['actualSpeed', 'expectedSpeed', 'contractSpeed'].map((field) => [field, { total: included.reduce((sum, point) => sum + (point[field] ?? 0), 0), divisor: duration, basis: 'minutes' }])
          );
          const average = (field) => (duration && included.some((point) => typeof point[field] === 'number') ? speedCalculations[field].total / duration : null);
          return [
            item.key,
            {
              ...summaryByArea[item.key],
              fullRangeEfficiency: summaries[item.key].fullRangeEfficiency,
              fullRangeContractEfficiency: summaries[item.key].fullRangeContractEfficiency,
              speedCalculations,
              actualSpeedAverage: average('actualSpeed'),
              expectedSpeedAverage: average('expectedSpeed'),
              contractSpeedAverage: average('contractSpeed')
            }
          ];
        })
      )
    };
  }, [dataByArea, datasets, efficiencyDataByArea, summaryByArea]);
  const netRunningMinutes = production.days
    ? production.effectiveMinutes -
      production.days.reduce((sum, day) => {
        if (!day.productionStart) return sum;
        return sum + getExcludedMinutes(Date.parse(day.productionStart), Date.parse(day.productionEnd) + 60_000, exclusionsByArea[dataset.key] ?? EMPTY_ENTRIES, noFlowEntries);
      }, 0)
    : undefined;
  const toggleGap = (key) =>
    setExcludedKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const toggleStop = (stop) =>
    setExcludedKeys((current) => {
      const covered = gaps.filter((gap) => gap.start < stop.end && gap.end > stop.start);
      const exclude = covered.some((gap) => Boolean(gap.defaultExcluded) === current.has(gap.key));
      const next = new Set(current);
      covered.forEach((gap) => {
        if (Boolean(gap.defaultExcluded) !== exclude) next.add(gap.key);
        else next.delete(gap.key);
      });
      return next;
    });
  return {
    toggleStop,
    unassignedStopGaps,
    unassignedStopMinutes: unassignedStopGaps.reduce((sum, gap) => sum + gap.minutes, 0),
    gaps,
    excludedGaps,
    noFlowEntries,
    exclusionsByArea,
    efficiencyDataByArea,
    summaryByArea: adjustedSummaryByArea,
    netRunningMinutes,
    toggleGap,
    clearGaps: () => setExcludedKeys(new Set())
  };
}
